import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react"
import { Link, NavLink, type LinkProps } from "react-router-dom"
import Orbitas from "@/src/components/web/Orbitas"

export const adminEase = [0.16, 1, 0.3, 1] as const

/** Marco del panel y del acceso: la página clara de la web con los anillos del símbolo de fondo, quietos. */
export function AdminShell({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`w-admin relative ${className}`}>
      <Orbitas />
      <div className="relative z-10">{children}</div>
    </div>
  )
}

export function AdminSpinner({ className = "" }: { className?: string }) {
  return <div className={`w-admin-girador ${className}`} role="status" aria-label="Cargando" />
}

export function AdminPageHeader({
  title,
  description,
  action,
  eyebrow = "Panel Owtli",
}: {
  title: string
  description?: string
  action?: ReactNode
  eyebrow?: string
}) {
  return (
    <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className="w-ceja mb-2">{eyebrow}</p>
        <h1 className="text-3xl md:text-[40px] font-semibold leading-[1.05] text-white">{title}</h1>
        {description && <p className="mt-3 text-[15px] text-white/60 max-w-xl leading-relaxed">{description}</p>}
      </div>
      {action}
    </div>
  )
}

export function AdminGlass({
  children,
  className = "",
  hover = false,
}: {
  children: ReactNode
  className?: string
  hover?: boolean
}) {
  return (
    <div
      className={`glass-card rounded-2xl ${hover ? "card-interactive" : ""} ${className}`}
    >
      {children}
    </div>
  )
}

export function AdminInput({
  className = "",
  ...props
}: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`admin-input ${className}`} {...props} />
}

export function AdminTextarea({
  className = "",
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`admin-input min-h-[100px] resize-y ${className}`} {...props} />
}

type AdminButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "ghost" | "danger" | "cyan"
}

export function AdminButton({
  variant = "primary",
  className = "",
  children,
  ...props
}: AdminButtonProps) {
  const variants = {
    primary: "btn-ocean",
    ghost: "btn-ghost",
    danger:
      "inline-flex items-center justify-center min-h-10 rounded-full px-5 text-sm font-semibold text-red-700 border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 transition-colors",
    cyan: "admin-btn-cyan",
  }
  return (
    <button type={props.type ?? "button"} className={`${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  )
}

export function AdminNavLink({
  to,
  icon,
  children,
  end,
}: {
  to: string
  icon: ReactNode
  children: ReactNode
  end?: boolean
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `admin-nav-link ${isActive ? "admin-nav-link--active" : ""}`
      }
    >
      {icon}
      <span>{children}</span>
    </NavLink>
  )
}

export function AdminBackLink({ to, children }: LinkProps & { children: ReactNode }) {
  return (
    <Link to={to} className="admin-back-link">
      {children}
    </Link>
  )
}
